import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AuthContext from '../contexts/AuthContext';

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }
  return new Date(value).toLocaleString();
}

function formatValue(value) {
  if (value === null || value === undefined) {
    return 'None';
  }
  const normalized = String(value).trim();
  return normalized || 'None';
}

function DetailItem({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{formatValue(value)}</Typography>
    </Box>
  );
}

function ReadStatusChip({ message }) {
  if (!message) {
    return null;
  }
  return (
    <Chip
      color={message.is_read ? 'success' : 'default'}
      label={message.is_read ? 'Read' : 'Unread'}
      variant={message.is_read ? 'filled' : 'outlined'}
      size="small"
    />
  );
}

export default function LabAccessionDetail() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const navigate = useNavigate();
  const { accessionId } = useParams();
  const { tokens } = useContext(AuthContext);

  const [accession, setAccession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tokens?.id_token || !accessionId) {
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError('');

    fetch(`${apiBase}/lab/accessions/${accessionId}`, {
      headers: {
        Authorization: `Bearer ${tokens.id_token}`,
      },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to load accession');
        }
        return response.json();
      })
      .then((data) => {
        setAccession(data);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Failed to load accession');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [accessionId, apiBase, tokens?.id_token]);

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5">Accession Detail</Typography>
            <Typography variant="body2" color="text.secondary">
              Review sample, workflow, patient, and rejection details for this accession.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/lab-accessioning')}>
            Back to Accessioning
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !accession ? (
          <Alert severity="info">Accession not found.</Alert>
        ) : (
          <Stack spacing={2}>
            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="h6">{formatValue(accession.sample_id)}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Accession ID: {accession.id}
                    </Typography>
                  </Box>
                  <Chip
                    color={accession.status === 'Rejected' ? 'error' : 'default'}
                    label={formatValue(accession.status)}
                    variant={accession.status === 'Rejected' ? 'filled' : 'outlined'}
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  <DetailItem label="Created By" value={accession.created_by_name || accession.created_by_user_id} />
                  <DetailItem label="Created" value={formatDateTime(accession.created_at)} />
                  <DetailItem label="Updated" value={formatDateTime(accession.updated_at)} />
                  <DetailItem label="Workflow" value={accession.workflow_name} />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Sample Details
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  <DetailItem label="Sample ID" value={accession.sample_id} />
                  <DetailItem label="Sample Barcode" value={accession.sample_barcode} />
                  <DetailItem label="Requisition Barcode" value={accession.requisition_barcode} />
                  <DetailItem label="Case ID" value={accession.lab_test_case_id} />
                  <DetailItem label="Order ID" value={accession.order_id} />
                  <DetailItem label="Barcode Match" value={accession.barcode_match === null ? 'Unknown' : accession.barcode_match ? 'Matched' : 'Mismatched'} />
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Patient And Practice
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 2,
                  }}
                >
                  <DetailItem
                    label="Patient"
                    value={[accession.patient_first_name, accession.patient_last_name].filter(Boolean).join(' ')}
                  />
                  <DetailItem label="DOB" value={accession.patient_dob} />
                  <DetailItem label="Provider" value={accession.provider_name} />
                  <DetailItem label="Practice" value={accession.practice_name} />
                  <DetailItem label="Practice NPI" value={accession.practice_npi} />
                  <DetailItem label="Provider Address" value={accession.provider_address} />
                </Box>
                {accession.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {accession.notes}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {accession.is_rejected && (
              <>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Rejection Details
                    </Typography>
                    {accession.rejection_reasons?.length ? (
                      <Stack spacing={1}>
                        {accession.rejection_reasons.map((reason) => (
                          <Box
                            key={reason.id}
                            sx={{
                              border: '1px solid #e5e7eb',
                              borderRadius: 1.5,
                              px: 1.5,
                              py: 1.25,
                              bgcolor: '#fff7f7',
                            }}
                          >
                            <Typography variant="body2">{formatValue(reason.question_text)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Logged {formatDateTime(reason.created_at)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No rejection reasons were recorded.
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={2}
                      alignItems={{ xs: 'flex-start', md: 'center' }}
                      justifyContent="space-between"
                      sx={{ mb: 2 }}
                    >
                      <Box>
                        <Typography variant="h6">Rejection Message</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Notification generated when this accession was rejected.
                        </Typography>
                      </Box>
                      <ReadStatusChip message={accession.rejection_message} />
                    </Stack>

                    {accession.rejection_message ? (
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: 1.5,
                            px: 1.5,
                            py: 1.5,
                            bgcolor: '#f8fafc',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                            Sent To
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                              gap: 2,
                            }}
                          >
                            <DetailItem label="Provider Name" value={accession.rejection_message.to_user_name || accession.provider_name} />
                            <DetailItem label="Provider Email" value={accession.rejection_message.to_user_email} />
                            <DetailItem label="Provider NPI" value={accession.rejection_message.to_user_provider_npi} />
                            <DetailItem label="Practice Name" value={accession.rejection_message.organization_name || accession.practice_name} />
                            <DetailItem label="Practice Email" value={accession.rejection_message.organization_email} />
                            <DetailItem label="Practice NPI" value={accession.rejection_message.organization_npi || accession.practice_npi} />
                            <DetailItem label="Message ID" value={accession.rejection_message.id} />
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 2,
                          }}
                        >
                          <DetailItem label="Subject" value={accession.rejection_message.subject} />
                          <DetailItem label="Sent" value={formatDateTime(accession.rejection_message.created_at)} />
                          <DetailItem label="First Read" value={formatDateTime(accession.rejection_message.first_read_at)} />
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Message
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {accession.rejection_message.message || 'No message body.'}
                          </Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No rejection message is linked to this accession.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Container>
  );
}
